import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "providers/Layout";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Plus, Users02 } from "untitledui-js-base";
import { InviteNewMemberModal } from "./components/InviteNewMemberModal";
import { useState } from "react";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { UsersTable } from "./components/UsersTable";
import { RemoveUserModal } from "./components/RemoveUserModal";
import { Loader } from "shared/components/Loader";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { instance } from "api/apiBase";
import { Pagination } from "shared/components/Pagination";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { User } from "./types";
import { useAuthContext } from "providers/Auth";

const query = "/portal/v1/users";

export const UsersView = () => {
  const { auth } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      page: "1",
      per_page: "10",
      order_direction: "desc"
    },
    ["page", "per_page", "order_direction"]
  );

  const { data: usersData, isLoading } = useQuery({
    queryKey: [query, { ...searchParams }],
    queryFn: () =>
      instance
        .get(query, {
          params: {
            page: searchParams.page,
            per_page: searchParams.per_page,
            order_direction: searchParams.order_direction
          }
        })
        .then((d) => d.data)
  });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState<User | undefined>();

  const queryClient = useQueryClient();

  const { mutate: deleteUser } = useMutation({
    mutationKey: ["DELETE_USER", userToRemove?.id],
    mutationFn: () => {
      return instance.delete(`/portal/v1/users/${userToRemove?.id}`);
    },
    onSuccess: () => {
      if (auth?.id === userToRemove?.id) window.location.reload();

      setUserToRemove(undefined);
      queryClient.invalidateQueries({
        queryKey: [query, { ...searchParams }]
      });
      toast.success("User was successfully removed");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  if (isLoading)
    return (
      <Layout
        sectionClassName={
          usersData?.length ? "" : "flex justify-center items-center"
        }
        header={
          <>
            <div className="flex items-center justify-between border-b border-alpha-40 px-3 md:px-6 pb-3">
              <BreadCrumbs
                crumbs={[
                  {
                    title: "Settings",
                    href: "/settings"
                  },
                  {
                    title: "Users",
                    href: "/settings/users"
                  }
                ]}
              />
            </div>
            <div className="flex items-center justify-between p-6 pb-3">
              <h1 className="text-xl font-bold text-content-1000">Users</h1>
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                Icon={Plus}
                onClick={() => setShowInviteModal(true)}
              >
                Invite user
              </Button>
            </div>
          </>
        }
      >
        <Loader />
      </Layout>
    );

  return (
    <Layout
      sectionClassName={
        usersData?.items?.length ? "" : "flex justify-center items-center"
      }
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 px-3 md:px-6 pb-3">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Settings",
                  href: "/settings"
                },
                {
                  title: "Users",
                  href: "/settings/users"
                }
              ]}
            />
          </div>
          <div className="flex items-center justify-between p-6 pb-3">
            <h1 className="text-xl font-bold text-content-1000">Users</h1>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              Icon={Plus}
              onClick={() => setShowInviteModal(true)}
            >
              Invite user
            </Button>
          </div>
        </>
      }
    >
      {usersData?.items?.length ? (
        <>
          <UsersTable
            users={usersData?.items}
            onRemoveUserClick={setUserToRemove}
          />
          <div className="ml-auto mt-10 flex max-w-[calc(100vw-106px)]">
            <Pagination
              className="justify-end"
              total={usersData?.page_count as number}
              current={Number(searchParams.page)}
              onChange={(e) =>
                setSearchParams({ ...searchParams, page: String(e) })
              }
            />
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center">
          <div className="mb-6 flex items-center justify-center rounded-full bg-alpha-40 p-7">
            <Users02 size="40" className="stroke-content-400" />
          </div>
          <p className="mb-2 text-base font-bold text-content-500">
            Team members will appear here
          </p>
          <p className="mb-6 text-sm font-medium text-content-300">
            You can invite team members to access your Boom system{" "}
          </p>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            onClick={() => setShowInviteModal(true)}
          >
            Invite a New Member
          </Button>
        </div>
      )}
      {userToRemove ? (
        <RemoveUserModal
          user={userToRemove}
          onClose={() => setUserToRemove(undefined)}
          onRemove={deleteUser}
        />
      ) : null}
      <InviteNewMemberModal
        opened={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        queryKey={[query, { ...searchParams }]}
      />
    </Layout>
  );
};
